import React from 'react';
import { BRAND } from '../../branding';
import { usePharmacyBranding } from '../../hooks/usePharmacyBranding';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

interface MonographSection {
  section: string;
  text: string;
}

interface Monograph {
  monographId: number;
  sections: MonographSection[];
}

interface CounselingMessage {
  code: number;
  importance: number;
  professionalMessage: string;
  patientMessage: string;
}

export interface PatientEdPrintDialogProps {
  open: boolean;
  onClose: () => void;
  drugName: string;
  din?: string;
  patientName?: string;
  monographs: Monograph[];
  counselingMessages?: CounselingMessage[];
}

const PatientEdPrintDialog: React.FC<PatientEdPrintDialogProps> = ({
  open,
  onClose,
  drugName,
  din,
  patientName,
  monographs,
  counselingMessages = [],
}) => {
  const { enqueueSnackbar } = useSnackbar();
  // PRX-26: the pharmacy's uploaded logo, else the build-time brand.
  const branding = usePharmacyBranding();

  // Strip non-printable / control characters that cause garbled text on print
  const sanitize = (s: string) =>
    // biome-ignore lint/suspicious/noControlCharactersInRegex: intentional strip of non-printable control characters for clean printing
    s.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]/g, '');

  const handlePrint = () => {
    // Build plain HTML string with text content safely escaped
    const esc = (s: string) => {
      const el = document.createElement('span');
      el.textContent = sanitize(s);
      return el.innerHTML;
    };

    const metaParts: string[] = [];
    if (din) metaParts.push(`DIN: ${esc(din)}`);
    if (patientName) metaParts.push(`Patient: ${esc(patientName)}`);
    metaParts.push(`Date: ${new Date().toLocaleDateString()}`);

    let sectionsHtml = '';
    for (const mono of monographs) {
      for (const sec of mono.sections) {
        sectionsHtml += `<p class="section-text">${esc(sec.text)}</p>`;
      }
    }

    let counselHtml = '';
    if (counselingMessages.length > 0) {
      counselHtml = '<div class="counseling-section"><h2>Important Counseling Information</h2>';
      for (const msg of counselingMessages) {
        counselHtml += `<div class="counseling-msg">\u2022 ${esc(msg.patientMessage)}</div>`;
      }
      counselHtml += '</div>';
    }

    const html = [
      '<!DOCTYPE html><html><head><meta charset="utf-8">',
      `<title>Medication Information - ${esc(drugName)}</title>`,
      '<style>',
      // GP-1333: no max-width — the sheet must use the full paper width dynamically
      'body { font-family: Arial, sans-serif; padding: 24px; margin: 0; color: #222; }',
      '.pharmacy-brand { text-align: center; margin-bottom: 8px; }',
      '.pharmacy-brand img { height: 36px; width: auto; margin-bottom: 4px; }',
      '.pharmacy-brand .name { font-size: 1.1em; font-weight: 700; color: #1976d2; }',
      '.header { border-bottom: 2px solid #1976d2; padding-bottom: 12px; margin-bottom: 16px; }',
      '.header h1 { font-size: 1.5em; margin: 0 0 4px 0; }',
      '.header .meta { font-size: 0.85em; color: #666; }',
      '.section-title { color: #1976d2; text-transform: uppercase; font-size: 0.85em; font-weight: 700; letter-spacing: 0.5px; margin: 16px 0 4px 0; }',
      '.section-text { line-height: 1.6; margin: 0 0 8px 0; white-space: pre-line; font-size: 0.95em; }',
      '.counseling-section { margin-top: 20px; padding-top: 16px; border-top: 1px solid #ccc; }',
      '.counseling-section h2 { font-size: 1.1em; margin: 0 0 8px 0; }',
      '.counseling-msg { margin-bottom: 6px; font-size: 0.9em; }',
      '.footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #ccc; font-size: 0.75em; color: #999; text-align: center; }',
      '@media print { body { padding: 12px; } }',
      '</style></head><body>',
      `<div class="pharmacy-brand"><img src="${branding.logo}" alt="${esc(BRAND.name)}" /></div>`,
      `<div class="header"><h1>${esc(drugName)}</h1><div class="meta">${metaParts.join('  |  ')}</div></div>`,
      sectionsHtml,
      counselHtml,
      `<div class="footer">This information is provided for educational purposes. Always follow your pharmacist&#39;s instructions.</div>`,
      '</body></html>',
    ].join('\n');

    // Use a hidden iframe to print — avoids popup blockers and about:blank issues
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.left = '-9999px';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDoc) {
      document.body.removeChild(iframe);
      return;
    }

    iframeDoc.open();
    iframeDoc.write(html);
    iframeDoc.close();

    iframe.onload = () => {
      iframe.contentWindow?.print();
      // Clean up after print dialog closes
      setTimeout(() => document.body.removeChild(iframe), 1000);
    };

    // Fallback if onload already fired (some browsers)
    setTimeout(() => {
      if (document.body.contains(iframe)) {
        iframe.contentWindow?.print();
        setTimeout(() => {
          if (document.body.contains(iframe)) document.body.removeChild(iframe);
        }, 1000);
      }
    }, 500);
  };

  const handleCopyToClipboard = async () => {
    const lines: string[] = [];

    lines.push(BRAND.name);
    lines.push('');

    lines.push(sanitize(drugName));
    const metaParts: string[] = [];
    if (din) metaParts.push(`DIN: ${din}`);
    if (patientName) metaParts.push(`Patient: ${patientName}`);
    metaParts.push(`Date: ${new Date().toLocaleDateString()}`);
    lines.push(metaParts.join('  |  '));
    lines.push('');

    for (const mono of monographs) {
      for (const sec of mono.sections) {
        lines.push(sanitize(sec.text));
        lines.push('');
      }
    }

    if (counselingMessages.length > 0) {
      lines.push('IMPORTANT COUNSELING INFORMATION');
      for (const msg of counselingMessages) {
        lines.push(`• ${sanitize(msg.patientMessage)}`);
      }
      lines.push('');
    }

    lines.push('This information is provided for educational purposes. Always follow your pharmacist\'s instructions.');

    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      enqueueSnackbar('Medication information copied to clipboard', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to copy to clipboard', { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" component="span" fontWeight={600}>
          Medication Information Sheet — Print Preview
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box
          sx={{
            p: 3,
            maxHeight: '65vh',
            overflow: 'auto',
            fontFamily: 'Arial, sans-serif',
          }}
        >
          {/* Pharmacy Branding */}
          <Box sx={{ textAlign: 'center', mb: 1 }}>
            <Box
              component="img"
              src={branding.logo}
              alt={BRAND.name}
              sx={{ height: 36, width: 'auto', mb: 0.5 }}
            />
          </Box>

          {/* Header */}
          <Box sx={{ borderBottom: '2px solid #1976d2', pb: 1.5, mb: 2 }}>
            <Typography variant="h5" fontWeight={700}>
              {drugName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {[
                din && `DIN: ${din}`,
                patientName && `Patient: ${patientName}`,
                `Date: ${new Date().toLocaleDateString()}`,
              ]
                .filter(Boolean)
                .join('  |  ')}
            </Typography>
          </Box>

          {/* Monograph content */}
          {monographs.map((mono) =>
            mono.sections.map((sec, idx) => (
              <Box key={`${mono.monographId}-${idx}`} sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                  {sanitize(sec.text)}
                </Typography>
              </Box>
            )),
          )}

          {/* Counseling messages */}
          {counselingMessages.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Important Counseling Information
              </Typography>
              {counselingMessages.map((msg) => (
                <Typography key={msg.code} variant="body2" sx={{ mb: 0.5 }}>
                  &bull; {msg.patientMessage}
                </Typography>
              ))}
            </>
          )}

          {/* Footer */}
          <Divider sx={{ mt: 3, mb: 1.5 }} />
          <Typography variant="caption" color="text.disabled" textAlign="center" display="block">
            This information is provided for educational purposes. Always follow your pharmacist&apos;s instructions.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        <Button
          variant="outlined"
          startIcon={<CopyIcon />}
          onClick={handleCopyToClipboard}
        >
          Copy to Clipboard
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientEdPrintDialog;
